import React, { useCallback, useState } from 'react';
import { Button, Input } from 'reactstrap';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { ChecklistEditorContext, ChecklistEditorDispatchContext } from '../checklist-editor-context';
import { Actions } from '../checklist-editor-reducer';
import { useAutosaveChanges } from '../utils';
import { DragIcon } from './icons';
import { useContextSelector } from 'use-context-selector';

export function ChoiceQuestionOption({
    questionId,
    optionId,
    showIcon,
    listItemIcon: ListItemIcon,
}) {
    const {
        setNodeRef,
        setActivatorNodeRef,
        transition,
        transform,
        attributes,
        listeners,
    } = useSortable({
        id: optionId,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    const dispatch = useContextSelector(ChecklistEditorDispatchContext, state => state);
    const option = useContextSelector(ChecklistEditorContext, state => state.optionsByQuestionId[questionId][optionId]);

    const [ isDirty, setIsDirty ] = useState(false);
    const [ label, setLabel ] = useState(() => option.label);

    const saveUncommittedChanges = useCallback(() => {
        if (!isDirty) {
            return;
        }

        const nextLabel = label.trim();
        if (nextLabel !== label) {
            setLabel(nextLabel);
        }

        dispatch({
            type: Actions.UPDATE_OPTION,
            payload: {
                optionId,
                questionId,
                label: nextLabel,
            },
        });

        setIsDirty(false);
    }, [ isDirty, dispatch, label, questionId ]);

    useAutosaveChanges(isDirty, saveUncommittedChanges);

    const renameLabel = (e) => {
        const value = e.target?.value ?? '';
        if (value === label) {
            return;
        }

        setLabel(value);
        setIsDirty(true);
    };

    const deleteOption = () => {
        dispatch({
            type: Actions.DELETE_OPTION,
            payload: {
                optionId,
                questionId,
            },
        });
    };

    return (<li
        className={ 'checklist-editor__choice-item' }
        key={ option.id }
        ref={ setNodeRef }
        style={ style }
    >
        <span
            className={ 'checklist-editor__drag-handle' }
            ref={ setActivatorNodeRef }
            { ...attributes }
            { ...listeners }
        >
            <DragIcon />
        </span>
        <span className={ 'checklist-editor__choice-icon' }>
            { showIcon && ListItemIcon && <ListItemIcon /> }
        </span>
        <Input
            name={ 'option' }
            type={ 'text' }
            value={ label }
            placeholder={ 'Change option label...' }
            onChange={ renameLabel }
            onBlur={ saveUncommittedChanges }
        />
        <span
            className={ 'checklist-editor__actions checklist-editor__actions--option' }
        >
            <Button
                close
                onClick={ deleteOption }
            >&times;</Button>
        </span>
    </li>);
}
